/** Pure Fleet display mapping — no I/O, no router actions. D4G. */

import type { StatusTone } from "./overview-ops";
import {
  DRIFT_KIND_LABELS,
  DRILL_LABELS,
  PHASE_LABELS,
  RING_LABELS,
  VERIFICATION_LABELS,
} from "./fleet-labels";

export type FleetSiteRow = {
  siteId: string;
  siteCode: string;
  siteName: string;
  ring: "pilot" | "canary" | "production";
  hasInventory: boolean;
  serialAssetTag: string | null;
  managementHost: string | null;
  routerModel: string | null;
  routerosVersion: string | null;
  hotspotProfileName: string | null;
  portalDirectoryPath: string | null;
  notes: string | null;
  inventoryUpdatedAt: string | null;
  driftKind: string | null;
  driftLabel: string | null;
  driftReason: string | null;
};

export type FleetDeployRow = {
  id: string;
  siteId: string;
  siteCode: string;
  siteName: string;
  ring: string;
  phase: string;
  expectedTemplateReleaseId: string;
  expectedTemplateSha256: string;
  artifactSha256: string | null;
  artifactFilename: string | null;
  intendedAt: string;
  installedReleaseId: string | null;
  installedSha256: string | null;
  verifiedAt: string | null;
  verificationStatus: string;
  evidenceNotes: string | null;
  approvedRollbackArtifactId: string | null;
  rollbackDrillStatus: string;
  driftReason: string | null;
  updatedAt: string;
};

export type FleetDriftListRow = {
  key: string;
  organizationId: string;
  organizationCode: string | null;
  siteId: string;
  siteCode: string;
  siteName: string;
  ring: string;
  expectedReleaseId: string | null;
  expectedTemplateSha256: string | null;
  recordedInstalledReleaseId: string | null;
  verificationStatus: string | null;
  deploymentId: string | null;
  deploymentPhase: string | null;
  intendedAt: string | null;
  driftKind: string;
  driftLabel: string;
  driftReason: string | null;
};

export type SiteSortKey = "name" | "code" | "ring" | "drift";
export type DeploySortKey = "intended" | "site" | "phase" | "release";
export type DriftSortKey = "site" | "kind" | "ring" | "intended";

export type StatusDisplay = { label: string; tone: StatusTone; detail?: string };

/** Honest ops banner — presentation only; does not change authority. */
export const FLEET_AUTHORITY_NOTE =
  "ناوگان فقط فراداده و قصد/شواهد دستی را نگه می‌دارد. قصد استقرار ≠ نصب کامل؛ بستهٔ آماده ≠ تأیید تولید؛ rc.21 تولید staged همچنان deferred/unexecuted است مگر دادهٔ موجود خلاف آن بگوید. اتوماسیون RouterOS همچنان NO-GO است.";

/** Inventory data-boundary copy — presentation only; same claims as prior fleet-banner. */
export const FLEET_DATA_BOUNDARY_NOTE =
  "موجودی فقط مدل، نسخه RouterOS، برچسب دارایی، میزبان مدیریت، پروفایل هات‌اسپات و مسیر پورتال را نگه می‌دارد. رمز، کلید خصوصی، توکن API، خروجی پیکربندی یا توپولوژی ذخیره نمی‌شود. انحراف «تأییدنشده» فقط نمایشی است.";

export function truncateSha256(value: string | null | undefined, head = 12): string {
  if (!value) return "—";
  if (value.length <= head + 1) return value;
  return `${value.slice(0, head)}…`;
}

export function ringDisplay(ring: string): StatusDisplay {
  const label = RING_LABELS[ring] ?? ring;
  if (ring === "production") {
    return {
      label,
      tone: "warning",
      detail:
        "برچسب حلقه تولید در CMS — استقرار خودکار را شروع نمی‌کند؛ اتوماسیون RouterOS NO-GO است",
    };
  }
  if (ring === "canary") return { label, tone: "info" };
  return { label, tone: "neutral" };
}

export function phaseDisplay(phase: string): StatusDisplay {
  const label = PHASE_LABELS[phase] ?? phase;
  if (phase === "intended") {
    return {
      label,
      tone: "info",
      detail: "قصد ثبت‌شده است — نصب روی روتر انجام‌شده محسوب نمی‌شود",
    };
  }
  if (phase === "installed_recorded") {
    return {
      label,
      tone: "warning",
      detail: "اظهار اپراتور از نصب دستی — تأیید خودکار RouterOS نیست",
    };
  }
  if (phase === "verified") return { label, tone: "success" };
  if (phase === "cancelled") return { label, tone: "neutral" };
  if (phase === "superseded") return { label, tone: "neutral" };
  return { label, tone: "neutral" };
}

export function verificationDisplay(status: string): StatusDisplay {
  const label = VERIFICATION_LABELS[status] ?? status;
  if (status === "verified_match") return { label, tone: "success" };
  if (status === "verified_mismatch") return { label, tone: "danger" };
  if (status === "unverified") return { label, tone: "info" };
  return { label, tone: "neutral" };
}

export function drillDisplay(status: string): StatusDisplay {
  return { label: DRILL_LABELS[status] ?? status, tone: "neutral" };
}

export function driftDisplay(kind: string, labelOverride?: string | null): StatusDisplay {
  const label = labelOverride || DRIFT_KIND_LABELS[kind] || kind;
  if (kind === "verified_mismatch") return { label, tone: "danger" };
  if (kind === "release_mismatch") return { label, tone: "warning" };
  if (kind === "unverified_stale") {
    return {
      label,
      tone: "info",
      detail: "نمایشی است — شکست یا اقدام خودکار روتر محسوب نمی‌شود",
    };
  }
  if (kind === "none") return { label, tone: "success" };
  return { label, tone: "neutral" };
}

export function buildFleetSiteRows(input: {
  sites: Array<{ id: string; code: string; name: string }>;
  inventoryBySite: Map<
    string,
    {
      ring: "pilot" | "canary" | "production";
      serialAssetTag: string | null;
      managementHost: string | null;
      routerModel: string | null;
      routerosVersion: string | null;
      hotspotProfileName: string | null;
      portalDirectoryPath: string | null;
      notes: string | null;
      updatedAt: string;
    }
  >;
  driftBySite: Map<
    string,
    { driftKind: string; driftLabel: string; driftReason: string | null }
  >;
}): FleetSiteRow[] {
  return input.sites.map((site) => {
    const inv = input.inventoryBySite.get(site.id);
    const drift = input.driftBySite.get(site.id);
    return {
      siteId: site.id,
      siteCode: site.code,
      siteName: site.name,
      ring: inv?.ring ?? "pilot",
      hasInventory: Boolean(inv),
      serialAssetTag: inv?.serialAssetTag ?? null,
      managementHost: inv?.managementHost ?? null,
      routerModel: inv?.routerModel ?? null,
      routerosVersion: inv?.routerosVersion ?? null,
      hotspotProfileName: inv?.hotspotProfileName ?? null,
      portalDirectoryPath: inv?.portalDirectoryPath ?? null,
      notes: inv?.notes ?? null,
      inventoryUpdatedAt: inv?.updatedAt ?? null,
      driftKind: drift?.driftKind ?? null,
      driftLabel: drift?.driftLabel ?? null,
      driftReason: drift?.driftReason ?? null,
    };
  });
}

export function toFleetDeployRow(
  d: {
    id: string;
    siteId: string;
    ring: string;
    phase: string;
    expectedTemplateReleaseId: string;
    expectedTemplateSha256: string;
    artifactSha256: string | null;
    artifactFilename: string | null;
    intendedAt: string;
    installedReleaseId: string | null;
    installedSha256: string | null;
    verifiedAt: string | null;
    verificationStatus: string;
    evidenceNotes: string | null;
    approvedRollbackArtifactId: string | null;
    rollbackDrillStatus: string;
    driftReason: string | null;
    updatedAt: string;
  },
  site: { code: string; name: string } | undefined,
): FleetDeployRow {
  return {
    id: d.id,
    siteId: d.siteId,
    siteCode: site?.code ?? d.siteId,
    siteName: site?.name ?? d.siteId,
    ring: d.ring,
    phase: d.phase,
    expectedTemplateReleaseId: d.expectedTemplateReleaseId,
    expectedTemplateSha256: d.expectedTemplateSha256,
    artifactSha256: d.artifactSha256,
    artifactFilename: d.artifactFilename,
    intendedAt: d.intendedAt,
    installedReleaseId: d.installedReleaseId,
    installedSha256: d.installedSha256,
    verifiedAt: d.verifiedAt,
    verificationStatus: d.verificationStatus,
    evidenceNotes: d.evidenceNotes,
    approvedRollbackArtifactId: d.approvedRollbackArtifactId,
    rollbackDrillStatus: d.rollbackDrillStatus,
    driftReason: d.driftReason,
    updatedAt: d.updatedAt,
  };
}

export function toFleetDriftListRow(row: {
  organizationId: string;
  organizationCode: string | null;
  siteId: string;
  siteCode: string;
  siteName: string;
  ring: string;
  expectedReleaseId: string | null;
  expectedTemplateSha256: string | null;
  recordedInstalledReleaseId: string | null;
  verificationStatus: string | null;
  deploymentId: string | null;
  deploymentPhase: string | null;
  intendedAt: string | null;
  driftKind: string;
  driftLabel: string;
  driftReason: string | null;
}): FleetDriftListRow {
  return {
    key: `${row.organizationId}:${row.siteId}:${row.deploymentId ?? "none"}`,
    ...row,
  };
}

export function matchesSiteQuery(row: FleetSiteRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    row.siteName,
    row.siteCode,
    row.ring,
    row.routerModel ?? "",
    row.managementHost ?? "",
    row.routerosVersion ?? "",
    row.serialAssetTag ?? "",
    row.driftKind ?? "",
    row.driftLabel ?? "",
  ]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function matchesSiteFilter(row: FleetSiteRow, filter: string): boolean {
  if (filter === "all") return true;
  if (filter === "drift") return Boolean(row.driftKind && row.driftKind !== "none");
  if (filter === "missing") return !row.hasInventory;
  return row.ring === filter;
}

export function filterAndSortSites(
  rows: FleetSiteRow[],
  opts: { query: string; filter: string; sort: SiteSortKey },
): FleetSiteRow[] {
  const filtered = rows.filter(
    (r) => matchesSiteQuery(r, opts.query) && matchesSiteFilter(r, opts.filter),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "code") return a.siteCode.localeCompare(b.siteCode, "en");
    if (opts.sort === "ring") return a.ring.localeCompare(b.ring);
    if (opts.sort === "drift") {
      return (a.driftKind ?? "none").localeCompare(b.driftKind ?? "none");
    }
    return a.siteName.localeCompare(b.siteName, "fa");
  });
  return sorted;
}

export function matchesDeployQuery(row: FleetDeployRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    row.siteCode,
    row.siteName,
    row.phase,
    row.expectedTemplateReleaseId,
    row.expectedTemplateSha256,
    row.artifactSha256 ?? "",
    row.installedReleaseId ?? "",
    row.verificationStatus,
    row.driftReason ?? "",
  ]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function matchesDeployFilter(row: FleetDeployRow, filter: string): boolean {
  if (filter === "all") return true;
  if (filter === "open") return row.phase === "intended" || row.phase === "installed_recorded";
  return row.phase === filter;
}

export function filterAndSortDeployments(
  rows: FleetDeployRow[],
  opts: { query: string; filter: string; sort: DeploySortKey },
): FleetDeployRow[] {
  const filtered = rows.filter(
    (r) => matchesDeployQuery(r, opts.query) && matchesDeployFilter(r, opts.filter),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "site") return a.siteCode.localeCompare(b.siteCode, "en");
    if (opts.sort === "phase") return a.phase.localeCompare(b.phase);
    if (opts.sort === "release") {
      return a.expectedTemplateReleaseId.localeCompare(b.expectedTemplateReleaseId, "en");
    }
    return b.intendedAt.localeCompare(a.intendedAt);
  });
  return sorted;
}

export function matchesDriftQuery(row: FleetDriftListRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    row.siteCode,
    row.siteName,
    row.driftKind,
    row.driftLabel,
    row.driftReason ?? "",
    row.expectedReleaseId ?? "",
    row.recordedInstalledReleaseId ?? "",
    row.organizationCode ?? "",
    row.ring,
  ]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function matchesDriftFilter(row: FleetDriftListRow, filter: string): boolean {
  if (filter === "all") return true;
  if (filter === "active") return row.driftKind !== "none";
  return row.driftKind === filter;
}

export function filterAndSortDrift(
  rows: FleetDriftListRow[],
  opts: { query: string; filter: string; sort: DriftSortKey },
): FleetDriftListRow[] {
  const filtered = rows.filter(
    (r) => matchesDriftQuery(r, opts.query) && matchesDriftFilter(r, opts.filter),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "kind") return a.driftKind.localeCompare(b.driftKind);
    if (opts.sort === "ring") return a.ring.localeCompare(b.ring);
    if (opts.sort === "intended") {
      return (b.intendedAt ?? "").localeCompare(a.intendedAt ?? "");
    }
    return a.siteCode.localeCompare(b.siteCode, "en");
  });
  return sorted;
}

export type RelatedFleetLink = { id: string; label: string; to: string };

export function relatedFleetLinks(): RelatedFleetLink[] {
  return [
    { id: "sites", label: "سایت‌ها", to: "/sites" },
    { id: "bundles", label: "Bundle", to: "/portal-bundles" },
    { id: "audit", label: "سابقه تغییرات", to: "/audit" },
    { id: "observability", label: "پایش عملیاتی", to: "/observability" },
  ];
}
